import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HandCoins } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { loansApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Loan, LoanStatus } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { LoanStatusBadge, Money, today } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';

const STATUSES: { value: LoanStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Being recovered' },
  { value: 'SUSPENDED', label: 'Paused' },
  { value: 'SETTLED', label: 'Settled' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/**
 * Staff loans and advances.
 *
 * <p>Payroll recovers an instalment from each run, capping the final one at
 * whatever is left. Cancelling means "issued in error" and is refused once
 * anything has been recovered; forgiving a balance is a write-off, which leaves
 * the repayment history intact so the loan keeps telling the truth about how much
 * was actually recovered.
 */
export function LoansPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('createdAt,desc');
  const manages = can(PERMISSIONS.LOAN_MANAGE);

  const [status, setStatus] = useState<LoanStatus | ''>('');
  const [issuing, setIssuing] = useState(false);
  const [viewing, setViewing] = useState<Loan | null>(null);

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [principal, setPrincipal] = useState('');
  const [instalment, setInstalment] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [reason, setReason] = useState('');

  const params = { ...table.params, ...(status === '' ? {} : { status }) };

  const loans = useQuery({
    queryKey: queryKeys.loans.list(params),
    queryFn: () => loansApi.list(params),
  });

  const detail = useQuery({
    queryKey: queryKeys.loans.detail(viewing?.id ?? ''),
    queryFn: () => loansApi.get(viewing!.id),
    enabled: viewing !== null,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.loans.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.hr.dashboard });
  };

  const issue = useMutation({
    mutationFn: () =>
      loansApi.issue({
        employeeId: employee!.id,
        principal: Number(principal),
        installmentAmount: Number(instalment),
        startDate,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: (loan) => {
      toast.success(`${loan.loanNumber} issued`);
      setIssuing(false);
      setEmployee(null);
      setPrincipal('');
      setInstalment('');
      setReason('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, next, why }: { id: string; next: LoanStatus; why: string }) =>
      loansApi.setStatus(id, next, why),
    onSuccess: (loan) => {
      toast.success(`${loan.loanNumber} is now ${loan.status.toLowerCase()}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const writeOff = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => loansApi.writeOff(id, { reason: why }),
    onSuccess: (loan) => {
      toast.success(`${loan.loanNumber}: balance written off`);
      setViewing(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Loan>[] = [
    {
      key: 'loanNumber',
      header: 'Loan',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.loanNumber}</span>
          <span className="block text-[11px] text-subtle">{formatDate(row.startDate)}</span>
        </span>
      ),
    },
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.employeeName)}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.employeeNumber)}
          </span>
        </span>
      ),
    },
    {
      key: 'principal',
      header: 'Advanced',
      sortKey: 'principal',
      className: 'text-right',
      render: (row) => <Money value={row.principal} />,
    },
    {
      key: 'instalment',
      header: 'Per month',
      className: 'text-right',
      render: (row) => <Money value={row.installmentAmount} />,
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      sortKey: 'outstanding',
      className: 'text-right',
      render: (row) => (
        <span>
          <Money value={row.outstanding} bold />
          {row.remainingInstalments !== undefined && row.remainingInstalments > 0 && (
            <span className="block text-[11px] text-muted">
              {row.remainingInstalments} more month{row.remainingInstalments === 1 ? '' : 's'}
            </span>
          )}
        </span>
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <LoanStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => (
        <span className="flex justify-end gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => setViewing(row)}>
            View
          </Button>
          {manages && (row.status === 'ACTIVE' || row.status === 'SUSPENDED') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const next: LoanStatus = row.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
                const why = window.prompt(
                  next === 'SUSPENDED'
                    ? 'Why is recovery being paused? (the loan is still owed)'
                    : 'Why is recovery resuming?',
                );
                if (why !== null && why.trim() !== '') {
                  changeStatus.mutate({ id: row.id, next, why: why.trim() });
                }
              }}
            >
              {row.status === 'ACTIVE' ? 'Pause' : 'Resume'}
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Staff Loans"
        description="Money advanced to staff, recovered from payroll a month at a time."
        icon={<HandCoins className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => setIssuing(true)}>Advance money</Button> : undefined}
        filters={
          <FilterSelect
            label="Status"
            value={status}
            onChange={(value) => {
              setStatus(value);
              table.setPage(0);
            }}
            options={STATUSES}
          />
        }
        pagination={loans.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={loans.data?.data}
          rowKey={(row) => row.id}
          loading={loans.isPending}
          error={loans.error}
          onRetry={() => void loans.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No loans"
          emptyDescription="Nothing has been advanced to staff."
        />
      </ListPage>

      {issuing && (
        <Modal open onClose={() => setIssuing(false)} title="Advance money to an employee">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              issue.mutate();
            }}
          >
            <EmployeePicker value={employee} onChange={setEmployee} />

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Amount advanced"
                type="number"
                step="0.01"
                min="1"
                required
                value={principal}
                onChange={(event) => setPrincipal(event.target.value)}
              />
              <TextField
                label="Instalment per month"
                type="number"
                step="0.01"
                min="1"
                required
                value={instalment}
                onChange={(event) => setInstalment(event.target.value)}
                hint="The last one is capped at whatever is left"
              />
            </div>

            {principal !== '' && instalment !== '' && Number(instalment) > 0 && (
              <p className="text-[13px] text-muted">
                Recovered over{' '}
                <span className="text-ink">
                  {Math.ceil(Number(principal) / Number(instalment))} payroll runs
                </span>
                .
              </p>
            )}

            <TextField
              label="Recovery starts from"
              type="date"
              required
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              hint="The first payroll period an instalment comes off"
            />

            <TextField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Medical advance, salary advance…"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setIssuing(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={employee === null || principal === '' || instalment === '' || issue.isPending}
              >
                {issue.isPending ? 'Issuing…' : 'Issue loan'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {viewing !== null && (
        <Modal open onClose={() => setViewing(null)} title={viewing.loanNumber}>
          <dl className="text-sm">
            <Row label="Employee" value={orDash(viewing.employeeName)} />
            <Row label="Advanced" value={<Money value={viewing.principal} />} />
            <Row label="Recovered so far" value={<Money value={detail.data?.repaid ?? viewing.repaid} />} />
            <Row label="Outstanding" value={<Money value={detail.data?.outstanding ?? viewing.outstanding} />} />
            <Row label="Instalment" value={<Money value={viewing.installmentAmount} />} />
            <Row label="Issued" value={`${formatDate(viewing.approvedAt)} by ${orDash(viewing.approvedByName)}`} />
            {viewing.reason !== undefined && <Row label="Notes" value={viewing.reason} />}
          </dl>

          <h3 className="mt-4 text-[11px] font-extrabold uppercase tracking-[0.06em] text-muted">
            What payroll has taken
          </h3>
          {(detail.data?.repayments ?? []).length === 0 ? (
            <p className="mt-1 text-[13px] text-muted">Nothing recovered yet.</p>
          ) : (
            <ul className="mt-1">
              {(detail.data?.repayments ?? []).map((repayment) => (
                <li
                  key={repayment.id}
                  className="flex items-baseline justify-between border-b border-line py-1.5 text-[13px] last:border-0"
                >
                  <span className="font-mono text-subtle">{repayment.slipNumber}</span>
                  <span className="text-ink">
                    <Money value={repayment.amount} /> → left{' '}
                    <Money value={repayment.balanceAfter} />
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex justify-end gap-2">
            {manages && viewing.outstanding > 0 && (
              <Button
                variant="danger"
                onClick={() => {
                  const why = window.prompt(
                    'Why is the balance being written off? No money moves — the repayments already made stay as they are.',
                  );
                  if (why !== null && why.trim() !== '') {
                    writeOff.mutate({ id: viewing.id, why: why.trim() });
                  }
                }}
              >
                Write off balance
              </Button>
            )}
            <Button onClick={() => setViewing(null)}>Close</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <dt className="text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}
